import { Component, JsonFile } from "projen";
import { TypeScriptProject } from "projen/lib/typescript";

/*******************************************************************************
 *
 * TsdocConfig
 *
 * Emits `tsdoc.json` at the project root so `eslint-plugin-tsdoc` recognises
 * the JSDoc tags consuming code commonly uses but that are not in the
 * default TSDoc tag list:
 *
 * - Workspace-scope package references (such as `@codedrifters` or
 *   `@openhi`) registered as modifier tags so prose like
 *   `See @codedrifters/configulator` does not trip `tsdoc-at-sign-in-word`.
 * - Common JSDoc convention tags (default, defaultValue, experimental, and
 *   the internal-marker tag) registered as modifier tags so they do
 *   not trip `tsdoc-undefined-tag`.
 *
 * Without `tsdoc.json` configulator emits, downstream consumers see ~1,250
 * `tsdoc-at-sign-in-word` warnings and ~17 `tsdoc-undefined-tag` warnings
 * per build on every scoped-package reference in a JSDoc comment.
 *
 * Note: the tag names above are intentionally written without their leading
 * at-sign. `tsc --stripInternal` (which configulator enables) treats the
 * file-leading banner as the JSDoc comment of the next declaration, and a
 * bare at-sign-prefixed `internal` token anywhere in that banner — even
 * inside a backtick code span — strips the following interface from the
 * emitted `.d.ts`.
 *
 ******************************************************************************/

/**
 * Configuration options for {@link TsdocConfig}.
 */
export interface TsdocConfigOptions {
  /**
   * Workspace scopes to register as TSDoc modifier tags so prose references
   * like `@codedrifters/configulator` are not treated as malformed TSDoc
   * tags.
   *
   * Each entry should include the leading `@` (e.g. `@codedrifters`,
   * `@openhi`). When omitted, the scope is derived from the consuming
   * project's `packageName` (the substring up to the first `/`, when the
   * name starts with `@`). `@codedrifters` is always included so internal
   * configulator references work out of the box.
   */
  readonly workspaceScopes?: Array<string>;

  /**
   * Additional modifier tag names (each starting with `@`) to register
   * beyond the standard set: default, defaultValue, experimental, and
   * the internal-marker tag.
   *
   * Note: the tag names above are intentionally written without their
   * leading at-sign. `tsc --stripInternal` (which configulator enables)
   * strips any declaration whose JSDoc contains the bare at-sign-prefixed
   * `internal` tag — even when the token sits inside a backtick code span
   * — and that strips this field from the published `.d.ts` rollup.
   *
   * @default []
   */
  readonly additionalModifierTags?: Array<string>;
}

/**
 * JSDoc convention modifier tags configulator registers in every emitted
 * `tsdoc.json`. `@default` is widely used across the npm ecosystem but is
 * not in the default `@microsoft/tsdoc` tag list. Built-in TSDoc tags
 * such as defaultValue, experimental, and the internal-marker tag are
 * intentionally NOT included — re-registering a built-in tag trips
 * `Error applying TSDoc configuration: A tag is already defined`, which
 * causes the parser to drop the entire custom config and flood lint with
 * cascading `tsdoc-undefined-tag` warnings on workspace-scope references.
 *
 * Note: the built-in tag names above are written without leading at-signs
 * for the same reason documented on `TsdocConfigOptions` — a bare
 * at-sign-prefixed `internal` token inside this banner would cause
 * `tsc --stripInternal` to strip the following declaration from the
 * emitted `.d.ts`.
 */
const STANDARD_MODIFIER_TAGS: Array<string> = ["@default"];

/**
 * JSDoc convention inline tags configulator registers in every emitted
 * `tsdoc.json`. `{@code …}` is widely used across the npm ecosystem but is
 * not in the default `@microsoft/tsdoc` tag list. `{@link …}` and
 * `{@inheritDoc …}` are intentionally omitted — they are built-in TSDoc
 * inline tags and re-registering them would trip the duplicate-definition
 * cascade described on `STANDARD_MODIFIER_TAGS`.
 */
const STANDARD_INLINE_TAGS: Array<string> = ["@code"];

/**
 * Always-on workspace scope. CodeDrifters packages cross-reference each
 * other often enough that this scope is registered on every project even
 * when the consumer's own package lives under a different scope.
 */
const ALWAYS_ON_SCOPES: Array<string> = ["@codedrifters"];

/**
 * Emits `tsdoc.json` so `eslint-plugin-tsdoc` recognises workspace-scope
 * package references and the standard JSDoc modifier tag set.
 */
export class TsdocConfig extends Component {
  /**
   * Derive a workspace scope from a scoped package name (`@scope/name`).
   * Returns `undefined` when the name is unscoped.
   *
   * @internal
   */
  public static scopeFromPackageName(
    packageName: string | undefined,
  ): string | undefined {
    if (!packageName || !packageName.startsWith("@")) {
      return undefined;
    }
    const slash = packageName.indexOf("/");
    if (slash <= 1) {
      return undefined;
    }
    return packageName.slice(0, slash);
  }

  constructor(project: TypeScriptProject, options: TsdocConfigOptions = {}) {
    super(project);

    /**
     * Build the workspace-scope set. Start from caller-provided scopes,
     * fall back to the project's own scope, and always include the
     * always-on set. Deduplicate so the emitted JSON is stable.
     */
    const scopeFromName = TsdocConfig.scopeFromPackageName(
      project.package.packageName,
    );
    const scopeSet = new Set<string>([
      ...ALWAYS_ON_SCOPES,
      ...(scopeFromName ? [scopeFromName] : []),
      ...(options.workspaceScopes ?? []),
    ]);

    /**
     * Build the modifier-tag set. Workspace scopes register as modifier
     * tags alongside the standard set + any additional caller-provided
     * names. Deduplicate to keep the emitted JSON deterministic.
     */
    const modifierTagSet = new Set<string>([
      ...STANDARD_MODIFIER_TAGS,
      ...Array.from(scopeSet),
      ...(options.additionalModifierTags ?? []),
    ]);

    /**
     * Inline-tag set. Currently fixed to `STANDARD_INLINE_TAGS` — kept as
     * a separate set so future additions follow the same dedupe/sort
     * shape as the modifier set.
     */
    const inlineTagSet = new Set<string>([...STANDARD_INLINE_TAGS]);

    /**
     * `tsdoc.json` schema: an array of tag definitions, each with a
     * `tagName` (starts with `@`), `syntaxKind` (`"modifier"` /
     * `"inline"` / `"block"`), and `allowMultiple`. Sort by `tagName`
     * across the combined set so the emitted file is stable across
     * synth runs. Built-in TSDoc tags must NOT appear here — see the
     * comment on `STANDARD_MODIFIER_TAGS` for the cascade this avoids.
     */
    const tagDefinitions = [
      ...Array.from(modifierTagSet).map((tagName) => ({
        tagName,
        syntaxKind: "modifier",
        allowMultiple: true,
      })),
      ...Array.from(inlineTagSet).map((tagName) => ({
        tagName,
        syntaxKind: "inline",
        allowMultiple: true,
      })),
    ].sort((a, b) => a.tagName.localeCompare(b.tagName));

    /**
     * `marker: false` — the TSDoc JSON schema is strict
     * (`additionalProperties: false`) and rejects projen's usual
     * `"//"` generated-by comment. Without this, `eslint-plugin-tsdoc`
     * fails to load `tsdoc.json`
     * (`Error loading config file: data must NOT have additional properties`),
     * falls back to the built-in tag set, and floods every release-workflow
     * run with cascading `tsdoc-undefined-tag` warnings. The
     * `linguist-generated` git attribute still flags the file as generated
     * in diffs.
     */
    new JsonFile(project, "tsdoc.json", {
      marker: false,
      obj: {
        $schema:
          "https://developer.microsoft.com/json-schemas/tsdoc/v0/tsdoc.schema.json",
        tagDefinitions,
      },
    });

    /**
     * `tsdoc.json` is a build-time lint config — it does not belong in the
     * published npm tarball.
     */
    project.addPackageIgnore("tsdoc.json");
  }
}
